import React from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useQuery } from "@apollo/client";
import { TextField } from "../TextField";
import { Switch } from "../Switch";
import { DatePicker } from "../DatePicker";
import { AutocompleteField } from "../AutocompleteField";
import type { DocumentNode } from "graphql";
import type { CustomFieldsSchemaItem } from "../types";
import { countryOptions } from "@/shared/utils/countries";

export interface CustomFieldsProps {
  name: string;
  label?: string;
  query: DocumentNode;
  queryVariables:
    | Record<string, any>
    | ((params: {
        values: Record<string, any>;
        allValues?: Record<string, any>;
      }) => Record<string, any>);
  getSchema: (data: any) => CustomFieldsSchemaItem[];
  allValues?: Record<string, any>;
  disabled?: boolean;
  fullWidth?: boolean;
  spacing?: number;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

function getVariables(
  queryVariables: CustomFieldsProps["queryVariables"],
  allValues: Record<string, any>
): Record<string, any> {
  if (typeof queryVariables === "function") {
    return queryVariables({ values: allValues, allValues });
  }
  return queryVariables;
}

function CustomFieldInput({
  parentName,
  field,
  disabled,
}: {
  parentName: string;
  field: CustomFieldsSchemaItem;
  disabled?: boolean;
}) {
  const fieldName = `${parentName}.${field.name}`;
  const common = {
    name: fieldName,
    label: field.label,
    required: field.required,
    disabled,
    fullWidth: true,
  };

  switch (field.type) {
    case "TEXT":
      return (
        <TextField
          {...common}
          type="text"
          placeholder={field.placeholder}
          helperText={field.helperText}
        />
      );
    case "NUMBER":
      return (
        <TextField
          {...common}
          type="number"
          placeholder={field.placeholder}
          helperText={field.helperText}
        />
      );
    case "BOOLEAN":
      return <Switch {...common} />;
    case "DATE":
      return <DatePicker {...common} />;
    case "SELECT":
      return (
        <AutocompleteField
          {...common}
          options={
            field.options?.map((o) => ({ value: o.value, label: o.label })) ??
            []
          }
          multiple={field.multiple ?? false}
        />
      );
    case "COUNTRY":
      return (
        <AutocompleteField
          {...common}
          options={countryOptions}
          placeholder={field.placeholder}
          multiple={field.multiple ?? false}
        />
      );
    default:
      return (
        <TextField
          {...common}
          type="text"
          placeholder={field.placeholder}
          helperText={field.helperText}
        />
      );
  }
}

export function CustomFields({
  name,
  label,
  query,
  queryVariables,
  getSchema,
  allValues = {},
  disabled,
  spacing = 2,
  xs = 12,
  sm,
  md,
  lg,
  xl,
}: CustomFieldsProps) {
  const variables = React.useMemo(
    () => getVariables(queryVariables, allValues),
    [queryVariables, allValues]
  );

  const shouldSkip = Object.values(variables).some(
    (v) => v === undefined || v === null || v === ""
  );

  const { data, loading, error } = useQuery(query, {
    variables,
    skip: shouldSkip,
  });

  const schema = React.useMemo(
    () => (data ? getSchema(data) : []),
    [data, getSchema]
  );

  if (shouldSkip) {
    return null;
  }

  if (loading) {
    return (
      <Grid size={{ xs, sm, md, lg, xl }}>
        <Typography color="text.secondary">Загрузка полей...</Typography>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid size={{ xs, sm, md, lg, xl }}>
        <Typography color="error">
          Ошибка загрузки схемы полей: {error.message}
        </Typography>
      </Grid>
    );
  }

  if (!schema || schema.length === 0) {
    return null;
  }

  return (
    <Grid size={{ xs, sm, md, lg, xl }}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      <Grid container spacing={spacing}>
        {schema.map((field) => (
          <Grid key={field.name} size={12}>
            <CustomFieldInput
              parentName={name}
              field={field}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}
